import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TBitValue,
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  ACTIVE_INACTIVE_CREDIT_PLAN,
  DELETE_CREDIT_PLAN,
  GET_ALL_CREDIT_PLAN,
} from "../../services/Endpoints";
import { ICreditPlanFilter } from "../../services/credit-plan/CreditPlanService";
import {
  DEFAULT_STATUS_CODE_BAD_REQUEST,
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastSuccess,
} from "../../utils/AppFunctions";
import { CREDIT_TYPE_ENUM, VISIBILITY_TYPE_ENUM } from "../../utils/PurchaseEnumerations";
import { formatAmountWithSign, formatCredits } from "../../utils/PurchaseFormat";

export interface ICreditPlan {
  id: string;
  name: string;
  credit_type: CREDIT_TYPE_ENUM;
  credits: number;
  visibility_type: VISIBILITY_TYPE_ENUM;
  base_price: string;
  cgst: string;
  sgst: string;
  igst: string;
  total: string;
  is_active: TBitValue;
}

export const CREDIT_PLAN_FILTER_INITIAL_VALUE: ICreditPlanFilter = {
  credit_type: null,
  visibility_type: null,
  is_active: null,
};

interface ICreditPlanCells {
  action: (plan: ICreditPlan) => JSX.Element;
  creditType: (plan: ICreditPlan) => JSX.Element;
  type: (plan: ICreditPlan) => JSX.Element;
}

export const getCreditPlanFields = (
  cells: ICreditPlanCells,
  handleStatus: (item: ICreditPlan) => void,
  t: TUseTranslationTfn,
): ITableFields<ICreditPlan>[] => [
  {
    name: t("name"),
    key: "name",
    sort: true,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-3",
  },
  {
    name: t("credit_type"),
    key: "credit_type",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditPlan>) => cells.creditType(data),
  },
  {
    name: t("credits"),
    key: "credits",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditPlan>) =>
      formatCredits(t, Number(data.credits)),
  },
  {
    name: t("type"),
    key: "visibility_type",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditPlan>) => cells.type(data),
  },
  {
    name: t("base_price"),
    key: "base_price",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditPlan>) => formatAmountWithSign(data.base_price),
  },
  {
    name: t("total"),
    key: "total",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<ICreditPlan>) => formatAmountWithSign(data.total),
  },
  {
    name: t("active"),
    key: "is_active",
    sort: false,
    type: TABLE_FIELD_TYPE.switch,
    align: TABLE_FIELD_ALIGN.center,
    checkedValue: BIT_VALUE.One,
    flex: "col-span-1",
    handleCheck: handleStatus,
  },
  {
    name: t("action"),
    key: "action",
    sort: false,
    type: TABLE_FIELD_TYPE.custom,
    align: TABLE_FIELD_ALIGN.center,
    flex: "col-span-2",
    component: cells.action,
  },
];

export const getAllCreditPlan = async (
  setCreditPlanList: TReactSetState<ICreditPlan[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  filter: ICreditPlanFilter,
) => {
  try {
    const response = await GET_ALL_CREDIT_PLAN(pagination, search, filter);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      setCreditPlanList(response.data.item);
      setPagination(response.data.pagination);
      return;
    }
    setCreditPlanList([]);
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "getAllCreditPlan");
  }
};

export const activeInactiveCreditPlan = async (id: string, value: TBitValue) => {
  try {
    const response = await ACTIVE_INACTIVE_CREDIT_PLAN(id, { is_active: value });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      toastSuccess(response.message);
      return true;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "activeInactiveCreditPlan");
  }
  return false;
};

export const deleteCreditPlan = async (id: string): Promise<string | null> => {
  try {
    const response = await DELETE_CREDIT_PLAN(id);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      return null;
    }
    if (response?.code === DEFAULT_STATUS_CODE_BAD_REQUEST && response.message) {
      return response.message;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
    return null;
  } catch (error) {
    handleError(error, "deleteCreditPlan");
    return null;
  }
};
